// src/mocks/handlers/filters/filtersSourcesNetworksHandler.ts

import { rest } from 'msw';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  IDynamicFiltersRequestBody,
  IDynFilterOption,
  IFundingFiltersResponse,
} from '@/domains/filters/filters.types';
import customerFilters from '@/mocks/data/customerFiltersSources.json';
import revenues from '@/mocks/data/fundingRevenueData.json';
import invoicesSummary from '@/mocks/data/fundingInvoicesSummaryData.json';
import commercialTerms from '@/mocks/data/commercialTerms.json';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

type CustomerFiltersMockEntry = {
  customer: string;
  sources?: IFundingFiltersResponse['sources'];
  networks?: IFundingFiltersResponse['networks'];
  agencies?: IFundingFiltersResponse['agencies'];
  invoice_agencies?: IFundingFiltersResponse['invoice_agencies'];
  commercial_terms_agencies?: IFundingFiltersResponse['commercial_terms_agencies'];
};

export const filtersSourcesNetworksHandler =
  rest.get<IDynamicFiltersRequestBody>(
    `${VITE_MSW_BASE_URL}${API_ENDPOINTS.CUSTOMER_FILTER_SOURCES}`,
    (req, res, ctx) => {
      const token = req.headers.get('Authorization');
      if (!token) {
        return res(
          ctx.status(500),
          ctx.json({ detail: 'Something went wrong' }),
        );
      }

      if (isAccessTokenExpired()) {
        return res(
          ctx.status(401),
          ctx.json({ error: 'Access token expired' }),
        );
      }

      const { customer_uuid } = req.params as { customer_uuid: string };

      const match = (customerFilters as CustomerFiltersMockEntry[]).find(
        (entry) => entry.customer === customer_uuid,
      );

      if (!match) {
        return res(
          ctx.status(404),
          ctx.json({ detail: 'Customer filters not found' }),
        );
      }

      const response: IFundingFiltersResponse = {};

      const toUniqueOptions = (
        labels: Array<string | null | undefined>,
      ): IDynFilterOption[] =>
        Array.from(
          new Set(
            labels
              .map((label) => label?.trim())
              .filter((label): label is string => Boolean(label)),
          ),
        ).map((label) => ({ uuid: label, label }));

      const fallbackRevenues = (
        revenues as Array<{
          uuid: string;
          results: Array<{ agency_name?: string | null }>;
        }>
      ).find((entry) => entry.uuid === customer_uuid);

      const fallbackInvoices = (
        invoicesSummary as Array<{
          uuid: string;
          results: Array<{ agency_name?: string | null }>;
        }>
      ).find((entry) => entry.uuid === customer_uuid);

      if (match.sources?.length) {
        response.sources = match.sources;
      }
      response.agencies = match.agencies?.length
        ? match.agencies
        : toUniqueOptions(
            fallbackRevenues?.results.map((row) => row.agency_name) ?? [],
          );
      response.invoice_agencies = match.invoice_agencies?.length
        ? match.invoice_agencies
        : toUniqueOptions(
            fallbackInvoices?.results.map((row) => row.agency_name) ?? [],
          );
      response.commercial_terms_agencies = match.commercial_terms_agencies
        ?.length
        ? match.commercial_terms_agencies
        : toUniqueOptions(
            (
              commercialTerms as Array<{
                customer_uuid: string;
                agency_name?: string | null;
              }>
            )
              .filter((row) => row.customer_uuid === customer_uuid)
              .map((row) => row.agency_name),
          );
      if (match.networks?.length) {
        response.networks = match.networks;
      }

      return res(ctx.status(200), ctx.json(response));
    },
  );
